import {
  fetchCompanyOptions,
  fetchBranchOptions,
  fetchDepartmentOptions,
} from '../../../api/filterOptionApi'

/**
 * pmOptionUtils.js
 * Centralized option formatters and normalizers for Project Management dropdowns & comboboxes.
 * Avoids duplicate code and ensures consistent display across all screens.
 */

/**
 * Normalizes a Job object into an AppCombobox/dropdown option { value, label, subLabel }.
 * Ensures human-readable job name is displayed primarily, with job code in subLabel.
 */
export function toJobOption(job) {
  if (!job) return null
  const id = job.id ?? job.value
  if (id === undefined || id === null) return null
  const name = String(job.job_name || job.name || job.title || job.label || '').trim()
  const code = String(job.job_code || job.code || job.subLabel || '').trim()
  const label = name || code || `Job #${id}`

  return {
    value: String(id),
    label,
    subLabel: code && code !== label ? code : undefined,
    projectName: job.project_name || job.projectName || '',
  }
}

/**
 * Normalizes a Task object into an AppCombobox/dropdown option { value, label, subLabel }.
 */
export function toTaskOption(task) {
  if (!task) return null
  const id = task.id ?? task.value
  if (id === undefined || id === null) return null
  const name = String(task.task_name || task.name || task.title || task.label || '').trim()
  const code = String(task.task_code || task.code || task.subLabel || '').trim()
  const label = name || code || `Task #${id}`

  return {
    value: String(id),
    label,
    subLabel: code && code !== label ? code : undefined,
    projectName: task.project_name || task.projectName || '',
  }
}

/**
 * Normalizes a Project object into an AppCombobox/dropdown option { value, label, subLabel }.
 */
export function toProjectOption(project) {
  if (!project) return null
  const id = project.id ?? project.value
  if (id === undefined || id === null) return null
  const name = String(project.project_name || project.name || project.title || project.label || '').trim()
  const code = String(project.project_code || project.code || project.subLabel || '').trim()
  const label = name || code || `Project #${id}`

  return {
    value: String(id),
    label,
    subLabel: code && code !== label ? code : undefined,
  }
}

/**
 * Utility to map a collection of items and optionally prepend an "All" option.
 */
export function buildSelectOptions(items, mapper, allLabel = 'All') {
  const normalized = (Array.isArray(items) ? items : []).map(mapper).filter(Boolean)
  if (allLabel) {
    return [{ value: 'All', label: allLabel }, ...normalized]
  }
  return normalized
}

/**
 * Normalizes a User object into an AppCombobox option { value, label, subLabel, id, name }.
 */
export function toUserOption(user) {
  if (!user) return null
  const id = user.id ?? user.userId ?? user.value
  if (id === undefined || id === null) return null
  const name = String(user.name || user.userName || user.email || user.label || '').trim()
  const code = String(user.employee_id || user.code || user.subLabel || '').trim()
  const label = name || code || `User #${id}`

  return {
    value: String(id),
    label,
    subLabel: code && code !== label ? code : undefined,
    id: Number(id),
    name: label,
  }
}

/**
 * Merges two arrays of user options deduplicated by numeric ID.
 */
export function mergeUserOptionsById(existing = [], incoming = []) {
  const map = new Map()
  ;(Array.isArray(existing) ? existing : []).forEach((item) => {
    const id = Number(item?.id ?? item?.userId ?? item?.value)
    if (Number.isFinite(id) && id > 0) map.set(id, item)
  })
  ;(Array.isArray(incoming) ? incoming : []).forEach((item) => {
    const id = Number(item?.id ?? item?.userId ?? item?.value)
    if (Number.isFinite(id) && id > 0) map.set(id, item)
  })
  return Array.from(map.values())
}

/**
 * Builds standard select options array with an optional 'All' item at index 0.
 */
export function buildUserSelectOptions(users, allLabel = 'All Assignees') {
  return buildSelectOptions(users, toUserOption, allLabel)
}

/**
 * Fetches organization filter options (companies, branches, departments) using
 * lightweight /options/* endpoints.
 * Returns formatted { companyOptions, branchOptions, departmentOptions } arrays
 * with prepended 'All' items.
 */
export async function fetchOrgFilterOptions({ companyIds = null, branchIds = null, pageSize = 200 } = {}) {
  const [compRes, branchRes, deptRes] = await Promise.allSettled([
    fetchCompanyOptions('', 1, pageSize),
    fetchBranchOptions('', 1, pageSize, companyIds),
    fetchDepartmentOptions('', 1, pageSize, companyIds, branchIds),
  ])

  const compItems = compRes.status === 'fulfilled' && Array.isArray(compRes.value?.items) ? compRes.value.items : []
  const branchItems = branchRes.status === 'fulfilled' && Array.isArray(branchRes.value?.items) ? branchRes.value.items : []
  const deptItems = deptRes.status === 'fulfilled' && Array.isArray(deptRes.value?.items) ? deptRes.value.items : []

  return {
    companyOptions: [{ value: 'All', label: 'All Companies' }, ...compItems],
    branchOptions: [{ value: 'All', label: 'All Branches' }, ...branchItems],
    departmentOptions: [{ value: 'All', label: 'All Departments' }, ...deptItems],
  }
}
